<template>
  <div class="sp-overlay" @click.self="requestClose">
    <div class="lsm" :class="{ 'lsm--project': isProjectHistory }">

      <!-- Header -->
      <header class="lsm-hdr">
        <span class="lsm-hdr__rule" aria-hidden="true"></span>
        <span class="lsm-hdr__title">{{ title }}</span>
        <span class="lsm-hdr__sep"></span>
        <span v-if="isProjectHistory" class="lsm-hdr__unit">
          <b>{{ row.name || '—' }}</b>
          <template v-if="row.developer"> · {{ row.developer }}</template>
        </span>
        <span v-else class="lsm-hdr__unit">
          Unit
          <b v-if="row.floor_plan && row.floor_plan.fid" class="lsm-unit-link"
            @click="$emit('floor-plan', row.floor_plan)">{{
              unitAddress }}</b>
          <b v-else>{{ unitAddress }}</b>
          <template v-if="row.mls_plan_name"> · Plan <b>{{ row.mls_plan_name }}</b></template>
        </span>
        <div class="lsm-hdr__date" v-if="isProjectHistory && salesStartDate">
          <span class="lsm-hdr__date-lbl">Sales Start</span>
          <span class="lsm-hdr__date-val">{{ salesStartDate }}</span>
        </div>
        <div class="lsm-hdr__date" v-else-if="!isProjectHistory && row.mls_list_date">
          <span class="lsm-hdr__date-lbl">List Date</span>
          <span class="lsm-hdr__date-val">{{ row.mls_list_date }}</span>
        </div>
        <button class="lsm-hdr__close" @click="requestClose" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      <!-- Specs strip — project scope -->
      <div v-if="isProjectHistory" class="lsm-specs">
        <div class="lsm-spec lsm-spec--wide">
          <span class="lsm-spec__lbl">Project</span>
          <span class="lsm-spec__val">{{ row.name || '—' }}</span>
        </div>
        <div class="lsm-spec lsm-spec--wide">
          <span class="lsm-spec__lbl">Developer</span>
          <span v-if="developerBadges.length" class="lsm-spec__val lsm-spec__val--badges">
            <span class="rpd-value-badge" v-for="name in developerBadges" :key="name">{{ name }}</span>
          </span>
          <span v-else class="lsm-spec__val">{{ row.developer || '—' }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">City</span>
          <span class="lsm-spec__val">{{ row.city || '—' }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Neighbourhood</span>
          <span class="lsm-spec__val">{{ row.neighbour || '—' }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Type</span>
          <span class="lsm-spec__val">{{ row.customTypeStoreyData || row.project_type || '—' }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Status</span>
          <span class="lsm-spec__val">{{ row.status || '—' }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Total Units</span>
          <span class="lsm-spec__val">{{ lsmNum(row.utc_units) }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Sales Start</span>
          <span class="lsm-spec__val">{{ row.sales_start_date || '—' }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Occupancy</span>
          <span class="lsm-spec__val">{{ row.occupancy_date || '—' }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Updates</span>
          <span class="lsm-spec__val">{{ projectRows.length }}</span>
        </div>
      </div>

      <!-- Specs strip -->
      <div v-else class="lsm-specs">
        <div class="lsm-spec lsm-spec--wide">
          <span class="lsm-spec__lbl">Unit</span>
          <span class="lsm-spec__val">{{ unitAddress }}<template v-if="row.mls_plan_name"> · {{
            row.mls_plan_name
              }}</template></span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Beds</span>
          <span class="lsm-spec__val">{{ formatBedsSpec(row.mls_beds) }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Baths</span>
          <span class="lsm-spec__val">{{ row.mls_baths || '—' }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Size</span>
          <span class="lsm-spec__val">{{ row.mls_unit_size ? Number(row.mls_unit_size).toLocaleString('en-CA') : '—' }}
            <span class="lsm-spec__muted">sqft</span></span>
        </div>
        <div v-if="isAdmin" class="lsm-spec">
          <span class="lsm-spec__lbl">Actual Size</span>
          <span class="lsm-spec__val">{{ row.mls_actual_size ? Number(row.mls_actual_size).toLocaleString('en-CA') : '—' }}
            <span v-if="row.mls_actual_size" class="lsm-spec__muted">sqft</span></span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">View</span>
          <span class="lsm-spec__val">{{ row.orientation || '—' }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Parking</span>
          <span class="lsm-spec__val" :class="{ 'lsm-spec__muted': !row.stalls }">{{ row.stalls || 'None' }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Stalls</span>
          <span class="lsm-spec__val">{{ row.mls_parking || '—' }}</span>
        </div>
        <div class="lsm-spec lsm-spec--wide">
          <span class="lsm-spec__lbl">Balcony</span>
          <span class="lsm-spec__val">
            <template v-if="row.balcony && row.balcony !== 'None'">
              {{ row.balcony }}<template v-if="row.balcony_area"> · {{ row.balcony_area }} <span
                  class="lsm-spec__muted">sqft</span></template>
            </template>
            <span v-else class="lsm-spec__muted">None</span>
          </span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">FP Specs</span>
          <span class="lsm-spec__val" :class="{ 'lsm-spec__muted': !row.fp_specs || row.fp_specs === 'None' }">{{
            (row.fp_specs && row.fp_specs !== 'None') ? row.fp_specs : '—' }}</span>
        </div>
        <div class="lsm-spec">
          <span class="lsm-spec__lbl">Levels</span>
          <span class="lsm-spec__val" :class="{ 'lsm-spec__muted': !row.levels }">{{ row.levels || '—' }}</span>
        </div>
      </div>

      <!-- Toolbar — project scope -->
      <div v-if="isProjectHistory" class="lsm-toolbar">
        <span class="lsm-toolbar__lbl">Filter</span>
        <button class="lsm-chip" :class="{ 'lsm-chip--on': filter === 'all' }" @click="filter = 'all'">
          All <span style="opacity:.7;margin-left:2px">· {{ projectRows.length }}</span>
        </button>
        <button class="lsm-chip" :class="{ 'lsm-chip--on': filter === 'sales' }" @click="filter = 'sales'">
          <span class="lsm-chip__dot"></span>{{ isMarketStrata ? 'Sales' : 'Leases' }}
        </button>
        <button class="lsm-chip" :class="{ 'lsm-chip--on': filter === 'incentives' }"
          @click="filter = 'incentives'">Incentives</button>
        <button class="lsm-chip" :class="{ 'lsm-chip--on': filter === 'notes' }" @click="filter = 'notes'">Notes &
          Files</button>
      </div>

      <!-- Toolbar -->
      <div v-else class="lsm-toolbar">
        <span class="lsm-toolbar__lbl">Filter</span>
        <button class="lsm-chip" :class="{ 'lsm-chip--on': filter === 'all' }" @click="filter = 'all'">
          All <span style="opacity:.7;margin-left:2px">· {{ history.length }}</span>
        </button>
        <button class="lsm-chip" :class="{ 'lsm-chip--on': filter === 'active' }" @click="filter = 'active'">
          <span class="lsm-chip__dot"></span>Active
        </button>
        <button class="lsm-chip" :class="{ 'lsm-chip--on': filter === 'sold' }" @click="filter = 'sold'">Sold</button>
        <button class="lsm-chip" :class="{ 'lsm-chip--on': filter === 'incentives' }"
          @click="filter = 'incentives'">Incentives</button>
        <span v-if="gridActive" class="lsm-grid-actions">
          <span v-if="!dirtyCount" class="lsm-grid-hint">Double-click a cell to edit</span>
          <span v-else class="lsm-grid-hint lsm-grid-hint--dirty">{{ dirtyCount }} unsaved row{{ dirtyCount !== 1 ? 's' : '' }}</span>
          <button v-if="dirtyCount" type="button" class="lsm-grid-btn lsm-grid-btn--ghost" :disabled="saving"
            @click="discardEdits">Discard</button>
          <button type="button" class="lsm-grid-btn" :disabled="!dirtyCount || saving" @click="saveEdits">
            {{ saving ? 'Saving…' : 'Save Changes' }}
          </button>
        </span>
        <button v-if="isAdmin" class="lsm-add-entry" :class="{ 'lsm-add-entry--inline': gridActive }"
          title="Add new MLS entry" @click="$emit('addMLSEntry', {
          projectId: latestCid,
          rowData: row,
          listingType: listingTypeValue,
        })">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
            stroke-linejoin="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add Entry
        </button>
      </div>

      <!-- Table — project scope. One row per `attr_history` update. -->
      <div v-if="isProjectHistory" class="lsm-table-wrap">
        <table class="lsm-table">
          <thead>
            <tr>
              <th>Update Date</th>
              <th>Source</th>
              <th class="num">Total</th>
              <th class="num">Rlsd</th>
              <th class="num">{{ isMarketStrata ? 'Sold' : 'Leased' }}</th>
              <th class="num">Δ {{ isMarketStrata ? 'Sold' : 'Leased' }}</th>
              <th class="num">Avail</th>
              <th class="num">{{ isMarketStrata ? 'Sold' : 'Leased' }} %</th>
              <th class="num">Avg Size</th>
              <th class="num">Avg Price</th>
              <th class="num">Gross $PSF</th>
              <th class="num">Net $PSF</th>
              <th class="num">Deposit</th>
              <th class="num">Buyer Inc.</th>
              <th class="num">Realtor Inc.</th>
              <th class="lsm-th-backup">Files</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in filteredProjectRows" :key="r.key">
              <td>{{ r.date || '—' }}</td>
              <td>
                <span v-if="r.source" class="lsm-type-pill">
                  <span class="lsm-type-pill__ring"></span>
                  {{ r.source }}
                </span>
                <span v-else class="lsm-muted">—</span>
              </td>
              <td class="num">{{ lsmNum(r.total) }}</td>
              <td class="num">{{ lsmNum(r.rlsd) }}</td>
              <td class="num"><b>{{ lsmNum(r.sold) }}</b></td>
              <td class="num">
                <span v-if="r.periodSold" class="lsm-delta"
                  :class="r.periodSold < 0 ? 'lsm-delta--down' : 'lsm-delta--up'">
                  <svg v-if="r.periodSold < 0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 5v14" />
                    <path d="M19 12l-7 7-7-7" />
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 19V5" />
                    <path d="M5 12l7-7 7 7" />
                  </svg>
                  {{ (r.periodSold > 0 ? '+' : '') + r.periodSold }}
                </span>
                <span v-else class="lsm-muted">—</span>
              </td>
              <td class="num">{{ lsmNum(r.avail) }}</td>
              <td class="num">{{ r.soldPct != null ? r.soldPct + '%' : '—' }}</td>
              <td class="num">{{ r.avgSize != null ? lsmNum(r.avgSize) + ' sf' : '—' }}</td>
              <td class="num">{{ lsmMoney(r.avgPrice) }}</td>
              <td class="num">{{ lsmMoney(r.grossPsf) }}</td>
              <td class="num"><b>{{ lsmMoney(r.netPsf) }}</b></td>
              <td class="num">{{ r.deposit != null ? r.deposit + '%' : '—' }}</td>
              <td class="num">
                <span v-if="r.buyerIncentives" class="lsm-incentive">{{ lsmMoney(r.buyerIncentives) }}</span>
                <span v-else class="lsm-muted">—</span>
              </td>
              <td class="num">
                <span v-if="r.realtorIncentives" class="lsm-incentive">{{ lsmMoney(r.realtorIncentives) }}</span>
                <span v-else class="lsm-muted">—</span>
              </td>
              <td class="lsm-td-backup">
                <a v-for="f in r.files" :key="f.index" :href="f.url" target="_blank" rel="noopener"
                  class="lsm-backup-link" :title="'Backup file ' + f.index">
                  <font-awesome-icon :icon="['fas', 'file-alt']" />
                </a>
                <span v-if="!r.files.length" class="lsm-muted">—</span>
              </td>
              <td class="lsm-td-note" :title="r.notes || ''">
                <span v-if="r.notes">{{ r.notes }}</span>
                <span v-else class="lsm-muted">—</span>
              </td>
            </tr>
            <tr v-if="!filteredProjectRows.length">
              <td :colspan="17" class="lsm-empty-row">No sales history recorded</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Table — editable grid. Opt-in via `editable` (Unit Mix) and admins
           only; everyone else keeps the read-only table below. -->
      <div v-else-if="gridActive" class="lsm-table-wrap lsm-grid-wrap">
        <div ref="gridEl" class="lsm-grid"></div>
      </div>

      <!-- Table -->
      <div v-else class="lsm-table-wrap">
        <table class="lsm-table">
          <thead>
            <tr>
              <th>Listing Type</th>
              <th>Status</th>
              <th>Price Date</th>
              <th v-if="!isRental" class="num">DOM</th>
              <th class="num">List Price</th>
              <th v-if="!isRental" class="num">$PSF</th>
              <th v-if="!isRental" class="num">Incentives</th>
              <th class="num">Net Price</th>
              <th class="num">Δ Net</th>
              <th v-if="!isRental" class="num">Net $PSF</th>
              <th>{{ isMarketStrata ? 'Sold Date' : 'Leased Date' }}</th>
              <th class="num">{{ isMarketStrata ? 'Sold Price' : 'Leased Price' }}</th>
              <th>Construction Status</th>
              <th>Completion Date</th>
              <th v-if="isAdmin" class="lsm-th-backup">Backup File</th>
              <th v-if="isAdmin" class="lsm-th-actions">Actions</th>
              <th v-if="isAdmin">Last Modified</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in filteredHistory" :key="i">
              <td>
                <span class="lsm-type-pill">
                  <span class="lsm-type-pill__ring"></span>
                  {{ customListingType(r) }}
                </span>
              </td>
              <td>
                <span class="lsm-status" :class="lsmStatusClass(r.mls_status)">{{ lsmStatusLabel(r.mls_status) }}</span>
              </td>
              <td>{{ r.mls_price_date || '—' }}</td>
              <td v-if="!isRental" class="num">{{ lsmDom(r.mls_price_date) }}</td>
              <td class="num" :style="priceBackUpStyle(r.price_back_up)">{{ lsmMoney(r.mls_list_price) }}</td>
              <td v-if="!isRental" class="num">{{ lsmPsf(r) }}</td>
              <td v-if="!isRental" class="num">
                <span v-if="Number(r.buyer_incentives) > 0">{{ lsmMoney(r.buyer_incentives) }}</span>
                <span v-else class="lsm-muted">—</span>
              </td>
              <td class="num"><b>{{ lsmMoney(getNetPrice(r)) }}</b></td>
              <td class="num">
                <span v-if="netDeltaPct(r) !== null" class="lsm-delta"
                  :class="netDeltaPct(r) < 0 ? 'lsm-delta--down' : 'lsm-delta--up'">
                  <svg v-if="netDeltaPct(r) < 0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 5v14" />
                    <path d="M19 12l-7 7-7-7" />
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 19V5" />
                    <path d="M5 12l7-7 7 7" />
                  </svg>
                  {{ (netDeltaPct(r) > 0 ? '+' : '') + netDeltaPct(r) + '%' }}
                </span>
                <span v-else class="lsm-muted">—</span>
              </td>
              <td v-if="!isRental" class="num">{{ lsmNetPsf(r) }}</td>
              <td>{{ r.mls_sold_date || '—' }}</td>
              <td class="num">{{ lsmMoney(r.mls_sold_price) }}</td>
              <td>
                <span v-if="r.construction_status">{{ r.construction_status }}</span>
                <span v-else class="lsm-muted">—</span>
              </td>
              <td>{{ lsmDate(r.completion_date) }}</td>
              <td v-if="isAdmin" class="lsm-td-backup">
                <a v-if="r.backup_file" :href="r.backup_file" target="_blank" rel="noopener" class="lsm-backup-link"
                  title="View Backup File">
                  <font-awesome-icon :icon="['fas', 'file-alt']" />
                </a>
                <span v-else class="lsm-muted">—</span>
              </td>
              <td v-if="isAdmin" class="lsm-td-actions">
                <span class="lsm-action lsm-action--edit" title="Edit MLS Entry" @click="$emit('editMLSEntryNew', {
                  projectId: r.cid,
                  projectEntry: r.entry,
                  rowData: r,
                  listingType: listingTypeValue,
                })">
                  <font-awesome-icon :icon="['fas', 'edit']" />
                </span>
                <span class="lsm-action-sep">|</span>
                <span class="lsm-action lsm-action--del" title="Delete MLS Entry" @click="$emit('deleteMLSEntry', {
                  projectId: r.cid,
                  projectEntry: r.entry,
                  rowData: r,
                  listingType: listingTypeValue,
                })">
                  <font-awesome-icon :icon="['fas', 'trash']" />
                </span>
              </td>
              <td v-if="isAdmin">{{ r.last_modified || '—' }}</td>
            </tr>
            <tr v-if="!filteredHistory.length">
              <td :colspan="emptyColspan" class="lsm-empty-row">No records</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Features & Costs — opt-in panel for the project scope. Open on load;
           the modal is height-capped, so the panel scrolls within its own
           bounds rather than pushing the KPI footer out of the dialog, and the
           header collapses it to hand the room back to the history table. -->
      <section v-if="featuresUid" class="lsm-fc">
        <button type="button" class="lsm-fc-head" :aria-expanded="featuresOpen ? 'true' : 'false'"
          @click="featuresOpen = !featuresOpen">
          <font-awesome-icon :icon="['fas', featuresOpen ? 'chevron-down' : 'chevron-right']" class="lsm-fc-caret" />
          <span>Features &amp; Costs</span>
        </button>
        <div v-if="featuresOpen" class="lsm-fc-body">
          <FeaturesCostsTab :uid="featuresUid" :raw-data="rawData || row" :project="row" :is-admin="isAdmin" />
        </div>
      </section>

      <!-- KPI footer — project scope -->
      <footer class="lsm-footer lsm-footer--project" v-if="isProjectHistory && projectKpis">
        <div class="lsm-kpi" v-for="k in projectKpis" :key="k.label">
          <div class="lsm-kpi__lbl">{{ k.label }}</div>
          <div class="lsm-kpi__val">{{ k.value }}</div>
          <div class="lsm-kpi__sub">{{ k.sub }}</div>
        </div>
      </footer>

      <!-- KPI footer -->
      <footer class="lsm-footer" v-else-if="!isProjectHistory && kpis">
        <div class="lsm-kpi">
          <div class="lsm-kpi__lbl">Original List</div>
          <div class="lsm-kpi__val">{{ kpis.originalList > 0 ? '$' + kpis.originalList.toLocaleString('en-CA') : '—' }}
          </div>
          <div class="lsm-kpi__sub" v-if="kpis.originalPsf">${{ kpis.originalPsf.toLocaleString('en-CA') }} / sqft</div>
        </div>
        <div class="lsm-kpi">
          <div class="lsm-kpi__lbl">Current Net</div>
          <div class="lsm-kpi__val">
            {{ kpis.currentNet > 0 ? '$' + kpis.currentNet.toLocaleString('en-CA') : '—' }}
            <span v-if="kpis.netDelta !== null" class="lsm-delta lsm-delta--inline"
              :class="kpis.netDelta < 0 ? 'lsm-delta--down' : 'lsm-delta--up'">
              <svg v-if="kpis.netDelta < 0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"
                stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 5v14" />
                <path d="M19 12l-7 7-7-7" />
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"
                stroke-linejoin="round">
                <path d="M12 19V5" />
                <path d="M5 12l7-7 7 7" />
              </svg>
              {{ Math.abs(kpis.netDelta).toFixed(2) }}%
            </span>
          </div>
          <div class="lsm-kpi__sub" v-if="kpis.currentPsf">${{ kpis.currentPsf.toLocaleString('en-CA') }} / sqft</div>
        </div>
        <div class="lsm-kpi">
          <div class="lsm-kpi__lbl">Price Updates</div>
          <div class="lsm-kpi__val">{{ kpis.updates }}</div>
          <div class="lsm-kpi__sub">{{ kpis.incentiveChanges }} incentive change{{ kpis.incentiveChanges !== 1 ? 's' :
            '' }}
          </div>
        </div>
      </footer>

    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import axios from 'axios';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_bootstrap4.min.css';
import { useUserStore } from '../../store/UserStore';
import { BACKEND_URL } from '../../variables';

// ── Editable grid helpers ──
// History entries carry dates as "05-Oct-25" (PHP d-M-y); the edit endpoint
// wants ISO. "-" and the epoch sentinel mean "not recorded".
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const toIsoDate = (v) => {
  const s = (v == null ? '' : String(v)).trim();
  if (!s || s === '-' || s === '31-Dec-69' || s === '1969-12-31' || s === '01-Jan-70' || s === '1970-01-01') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2})$/);
  if (!m) return '';
  const mi = MONTHS.findIndex((x) => x.toLowerCase() === m[2].toLowerCase());
  if (mi < 0) return '';
  const year = Number(m[3]) < 70 ? `20${m[3]}` : `19${m[3]}`;
  return `${year}-${String(mi + 1).padStart(2, '0')}-${m[1].padStart(2, '0')}`;
};
const fromIsoDate = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${MONTHS[Number(m[2]) - 1]}-${m[1].slice(2)}` : '';
};
// The entry fields the grid lets an admin change. Everything else on the row
// (DOM, $PSF, net, Δ net, listing type) is derived and recomputes on edit.
const GRID_EDITABLE = [
  'mls_status', 'mls_price_date', 'mls_list_price', 'buyer_incentives',
  'mls_sold_date', 'mls_sold_price', 'construction_status', 'completion_date',
  // unit-mix extras (columns exist only when opened from the Unit Mix tab)
  'mls_list_date', 'realtor_incentives', 'price_back_up', 'close_date', 'levels', 'mls_pid',
  'mls_actual_size',
];
// Stored once per unit, so an edit on one entry applies to every entry.
const UNIT_WIDE_FIELDS = ['completion_date', 'close_date'];
const LEVEL_TIDS = { None: '11106', '1 Level': '11074', '2 Level': '11075', '3 Level': '11076', '4 Level': '11077', '5 Level': '11113' };
const PRICE_STATUS_VALUES = { '': '—', Verified: 'Verified', 'Semi-Verified': 'Semi-Verified', 'Educated Estimated': 'Educated Estimated' };
const DATE_FIELDS = new Set(['mls_price_date', 'mls_sold_date', 'completion_date', 'mls_list_date', 'close_date']);
// Normalized for dirty checks: numbers come back from the editor as numbers,
// the originals as strings, and every empty date spelling is the same "none".
const normVal = (field, v) => {
  if (DATE_FIELDS.has(field)) return toIsoDate(v);
  return v == null ? '' : String(v).trim();
};
const STATUS_TIDS = { active: 8197, sold: 8198, leased: 8198, unreleased: 23440, none: 11482 };
const esc = (v) => String(v == null ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const DASH = '<span class="lsm-muted">—</span>';
const SVG_DOWN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>';
const SVG_UP = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>';
const SVG_FILE = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h8"/></svg>';
const SVG_EDIT = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>';
const SVG_TRASH = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>';

// Tabulator editor for the d-M-y date strings: a native date input, written
// back in the same format the rest of the history uses. `emptyValue` is what a
// cleared date is stored as ("-" for sold date, matching the API).
const dateEditor = (emptyValue) => (cell, onRendered, success, cancel) => {
  const input = document.createElement('input');
  input.type = 'date';
  input.className = 'lsm-grid-date';
  input.value = toIsoDate(cell.getValue());
  let done = false;
  const commit = () => {
    if (done) return;
    done = true;
    success(input.value ? fromIsoDate(input.value) : emptyValue);
  };
  onRendered(() => {
    input.focus();
    try { if (input.showPicker) input.showPicker(); } catch (e) { /* not user-activated */ }
  });
  // `change` fires per segment while a date is typed, so only a full,
  // plausible date commits early; blur / Enter commit whatever is there.
  input.addEventListener('change', () => {
    const y = Number((input.value || '').slice(0, 4));
    if (!input.value || y >= 1900) commit();
  });
  input.addEventListener('blur', commit);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') commit();
    else if (e.key === 'Escape') { done = true; cancel(); }
  });
  return input;
};

// `attr_history` values arrive as strings, and "-" / "" / null all mean "not
// recorded" — they must not read as 0.
const toNum = (v) => {
  if (v == null || v === '' || v === '-') return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
};

export default {
  name: 'ListingSummaryModal',
  props: {
    row: { type: Object, required: true },
    history: { type: Array, default: () => [] },
    isMarketStrata: { type: Boolean, default: false },
    isRental: { type: Boolean, default: false },
    title: { type: String, default: 'Listing Summary' },
    occupancy_date: { type: String, default: '' },
    sold_out_date: { type: String, default: '' },
    rawData: { type: Object, default: null },
    fromUnitMix: { type: Boolean, default: false },
    // Project scope: `history` carries the project's `attr_history` entries
    // (one per sales update) instead of MLS entries for a single unit, so the
    // specs strip, table and KPIs all switch to project-level figures.
    isProjectHistory: { type: Boolean, default: false },
    // Opt-in: adds a Features & Costs panel under the history table, keyed off
    // `row.uid`. Off by default so the unit-scoped callers (UnitMix, UnitReport,
    // MlsRental and the unit mix tabs) are unchanged.
    showFeaturesCosts: { type: Boolean, default: false },
    // Opt-in: admins get the MLS history as an editable Tabulator grid, saved
    // per entry through api/presale_mls_entry_edit. Off by default, so the
    // other unit-scoped callers keep the read-only table.
    editable: { type: Boolean, default: false },
  },
  emits: ['close', 'floor-plan', 'editMLSEntryNew', 'deleteMLSEntry', 'addMLSEntry', 'entries-saved'],
  components: {
    // Loaded on demand, so the callers that leave the panel off never pull it
    // into their bundle.
    FeaturesCostsTab: () => import('../project-detail/FeaturesCostsTab.vue'),
  },
  data() {
    return {
      filter: 'all',
      // The panel opens with the modal; the header toggle collapses it.
      featuresOpen: true,
      // Editable grid state. The row arrays are frozen so Vue does not observe
      // objects Tabulator owns; `gridVersion` is bumped instead to recompute.
      gridRows: Object.freeze([]),
      gridOriginals: Object.freeze({}),
      gridVersion: 0,
      saving: false,
      constructionStatusOptions: [],
    };
  },
  computed: {
    ...mapState(useUserStore, ['current_user']),
    isAdmin() {
      return (this.current_user?.role || '').toLowerCase() === 'admin';
    },
    gridActive() {
      return this.editable && this.isAdmin && !this.isProjectHistory;
    },
    dirtyCount() {
      void this.gridVersion;
      return this.gridData().filter((r) => this.rowEditedFields(r).length).length;
    },
    // The UTC id the Features & Costs panel loads from. Project rows carry it
    // as `uid`; without one there is nothing to fetch, so the panel is dropped.
    featuresUid() {
      if (!this.showFeaturesCosts) return '';
      return (this.row && this.row.uid) || '';
    },
    // One badge per developer term, so co-developers stay distinguishable.
    // Rows without developer_terms fall back to the plain `developer` text.
    developerBadges() {
      const terms = this.row && this.row.developer_terms;
      return Array.isArray(terms)
        ? terms.map((t) => t && t.name).filter(Boolean)
        : [];
    },
    unitAddress() {
      return this.fromUnitMix ? this.row.mls_address : this.row.field_mls_address_value;
    },
    listingTypeValue() {
      return this.isRental ? 'rental' : (this.isMarketStrata ? 'market_strata' : 'presale');
    },
    latestCid() {
      const lastWithCid = [...(this.history || [])].reverse().find(r => r && r.cid);
      return (lastWithCid && lastWithCid.cid) || (this.row && this.row.cid) || null;
    },
    emptyColspan() {
      // + construction status and completion date, which show for every scope
      const base = (this.isRental ? 8 : 12) + 2;
      return base + (this.isAdmin ? 3 : 0);
    },
    // `attr_history` entries normalized for display. The backend hands them over
    // as strings (and "-" for "not recorded"), so everything is coerced here;
    // `periodSold` is the units moved since the previous update, which is the
    // figure the history is actually read for.
    projectRows() {
      const entries = Array.isArray(this.history) ? this.history : [];
      return entries.map((item, i) => {
        const prev = i > 0 ? entries[i - 1] : null;
        const total = toNum(item.total);
        const rlsd = toNum(item.rlsd_units);
        const sold = toNum(item.sold_total);
        const prevSold = prev ? toNum(prev.sold_total) : null;
        const availRaw = toNum(item.available);
        const avgSize = toNum(item.avg_size);
        const grossPsf = toNum(item.avg_psf);
        const avgPrice =
          toNum(item.avg_price) ??
          (grossPsf != null && avgSize != null ? Math.round(grossPsf * avgSize) : null);
        return {
          key: item._key != null ? item._key : i,
          date: item.date || null,
          source: item.sold_update || null,
          notes: item.sold_update_notes || null,
          total,
          rlsd,
          sold,
          periodSold:
            sold != null && prevSold != null ? sold - prevSold : i === 0 ? sold : null,
          avail:
            availRaw != null
              ? availRaw
              : rlsd != null && sold != null
                ? Math.max(0, rlsd - sold)
                : null,
          soldPct: rlsd ? Math.round((sold || 0) / rlsd * 100) : null,
          avgSize,
          avgPrice,
          grossPsf,
          netPsf: toNum(item.total_net_psf),
          deposit: toNum(item.deposit),
          buyerIncentives: toNum(item.buyer_incentives),
          realtorIncentives: toNum(item.realtor_incentives),
          files: Array.isArray(item.files) ? item.files.filter((f) => f && f.url) : [],
        };
      });
    },
    filteredProjectRows() {
      const rows = this.projectRows;
      if (this.filter === 'sales') return rows.filter((r) => r.periodSold > 0);
      if (this.filter === 'incentives')
        return rows.filter((r) => r.buyerIncentives > 0 || r.realtorIncentives > 0);
      if (this.filter === 'notes') return rows.filter((r) => r.notes || r.files.length);
      return rows;
    },
    salesStartDate() {
      const rows = this.projectRows;
      return (rows.length && rows[0].date) || this.row.sales_start_date || '';
    },
    projectKpis() {
      const rows = this.projectRows;
      if (!rows.length) return null;
      // Updates leave gaps, so each KPI walks back from the newest entry to the
      // last one that actually recorded the field.
      const pick = (key) => {
        for (let i = rows.length - 1; i >= 0; i--) {
          if (rows[i][key] != null) return rows[i];
        }
        return null;
      };
      const totalRow = pick('total');
      const rlsdRow = pick('rlsd');
      const soldRow = pick('sold');
      const availRow = pick('avail');
      const netRow = pick('netPsf');
      const total = totalRow ? totalRow.total : null;
      const rlsd = rlsdRow ? rlsdRow.rlsd : null;
      const sold = soldRow ? soldRow.sold : null;
      const soldLbl = this.isMarketStrata ? 'Sold' : 'Leased';
      return [
        {
          label: 'Total Units',
          value: this.lsmNum(total),
          sub: 'in project',
        },
        {
          label: 'Released',
          value: this.lsmNum(rlsd),
          sub: total && rlsd != null ? Math.round(rlsd / total * 100) + '% of total' : '—',
        },
        {
          label: soldLbl,
          value: this.lsmNum(sold),
          sub: rlsd && sold != null ? Math.round(sold / rlsd * 100) + '% of released' : '—',
        },
        {
          label: 'Available',
          value: this.lsmNum(availRow ? availRow.avail : null),
          sub: 'remaining',
        },
        {
          label: 'Latest Net $PSF',
          value: this.lsmMoney(netRow ? netRow.netPsf : null),
          sub: netRow && netRow.date ? `as of ${netRow.date}` : '—',
        },
        {
          label: 'Updates',
          value: String(rows.length),
          sub: this.salesStartDate ? `since ${this.salesStartDate}` : '—',
        },
      ];
    },
    historyWithPrevNet() {
      return this.history.map((r, i) => ({
        ...r,
        previous_net_price: i > 0 ? this.getNetPrice(this.history[i - 1]) : 0,
      }));
    },
    filteredHistory() {
      const rows = this.historyWithPrevNet;
      if (this.filter === 'active') return rows.filter(r => (r.mls_status || '').toLowerCase() === 'active');
      if (this.filter === 'sold') return rows.filter(r => (r.mls_status || '').toLowerCase() === 'sold');
      if (this.filter === 'incentives') return rows.filter(r => Number(r.buyer_incentives) > 0);
      return rows;
    },
    kpis() {
      const history = this.history;
      const row = this.row;
      if (!history.length || !row) return null;
      const size = Number(row.mls_unit_size) || 1;
      const first = history.find(r => Number(r.mls_list_price) > 0);
      const last = [...history].reverse().find(r => Number(r.mls_list_price) > 0);
      const originalList = first ? Number(first.mls_list_price) : 0;
      // Baseline the delta on the original *net*, not the gross list price —
      // otherwise a listing that never changed price still reads as a drop
      // equal to its incentive.
      const originalNet = first ? this.getNetPrice(first) : 0;
      const currentNet = last ? this.getNetPrice(last) : 0;
      const rawDelta = originalNet > 0 ? ((currentNet - originalNet) / originalNet * 100) : null;
      const netDelta = rawDelta === null || Math.abs(rawDelta) < 0.005 ? null : rawDelta;
      const listDateStr = row.mls_list_date;
      let dom = null;
      if (listDateStr) {
        const ms = Date.now() - new Date(listDateStr).getTime();
        dom = Math.max(0, Math.round(ms / 86400000));
      }
      const incentiveChanges = history.filter(r => Number(r.buyer_incentives) > 0).length;
      return {
        originalList,
        originalPsf: originalList > 0 ? Math.round(originalList / size) : null,
        currentNet,
        currentPsf: currentNet > 0 ? Math.round(currentNet / size) : null,
        netDelta,
        dom,
        listDateStr,
        updates: history.length,
        incentiveChanges,
      };
    },
  },
  watch: {
    filter() {
      this.applyGridFilter();
    },
    // A new history array means a different unit (or a reload): rebuild.
    history() {
      if (!this.gridActive) return;
      this.buildGridRows();
      this.$nextTick(() => this.initGrid());
    },
    gridActive(on) {
      if (on) this.setupGrid();
      else this.destroyGrid();
    },
  },
  mounted() {
    if (this.gridActive) this.setupGrid();
  },
  beforeDestroy() {
    this.destroyGrid();
  },
  methods: {
    requestClose() {
      if (this.gridActive && this.dirtyCount &&
        !window.confirm('You have unsaved changes to this listing history. Close without saving?')) return;
      this.$emit('close');
    },

    // ── Editable grid ──
    setupGrid() {
      this.buildGridRows();
      if (!this.constructionStatusOptions.length) this.fetchConstructionStatuses();
      this.$nextTick(() => this.initGrid());
    },
    gridData() {
      return this._grid ? this._grid.getData() : this.gridRows;
    },
    pickEditable(r) {
      const o = {};
      GRID_EDITABLE.forEach((f) => { o[f] = r[f] == null ? '' : r[f]; });
      return o;
    },
    rowEditedFields(r) {
      const o = this.gridOriginals[r._k];
      if (!o) return [];
      return GRID_EDITABLE.filter((f) => normVal(f, r[f]) !== normVal(f, o[f]));
    },
    buildGridRows() {
      const rows = (this.history || []).map((r, i) => ({
        ...r,
        _i: i,
        _k: `${r.cid}:${r.entry != null ? r.entry : i}`,
      }));
      const originals = {};
      rows.forEach((r) => { originals[r._k] = this.pickEditable(r); });
      this.gridOriginals = Object.freeze(originals);
      this.gridRows = Object.freeze(rows);
      this.gridVersion++;
    },
    destroyGrid() {
      if (this._grid) {
        this._grid.destroy();
        this._grid = null;
      }
    },
    initGrid() {
      if (!this.gridActive || !this.$refs.gridEl) return;
      this.destroyGrid();
      const grid = new Tabulator(this.$refs.gridEl, {
        data: this.gridRows,
        index: '_k',
        layout: 'fitDataStretch',
        maxHeight: '52vh',
        placeholder: 'No records',
        editTriggerEvent: 'dblclick',
        // History reads chronologically (Δ Net is against the previous
        // entry), so the header sorters are off.
        columnDefaults: { headerSort: false, vertAlign: 'middle' },
        columns: this.gridColumns(),
        rowFormatter: (row) => {
          const edited = this.rowEditedFields(row.getData());
          row.getCells().forEach((cell) => {
            const f = cell.getColumn().getField();
            cell.getElement().classList.toggle('lsm-cell-edited', edited.includes(f));
          });
        },
      });
      grid.on('cellEdited', () => {
        this.gridVersion++;
        this.refreshGrid();
      });
      grid.on('tableBuilt', () => this.applyGridFilter());
      this._grid = grid;
    },
    // Derived cells (net, Δ net, listing type…) read other fields and other
    // rows, so every row is re-rendered after an edit. Histories are short.
    refreshGrid() {
      if (!this._grid) return;
      this._grid.getRows().forEach((row) => row.reformat());
    },
    matchesFilter(r) {
      const status = (r.mls_status || '').toLowerCase();
      if (this.filter === 'active') return status === 'active';
      if (this.filter === 'sold') return status === 'sold';
      if (this.filter === 'incentives') return Number(r.buyer_incentives) > 0;
      return true;
    },
    applyGridFilter() {
      if (!this._grid) return;
      if (this.filter === 'all') this._grid.clearFilter();
      else this._grid.setFilter((data) => this.matchesFilter(data));
    },
    gridNetDelta(d) {
      const prev = this.gridData().find((x) => x._i === d._i - 1);
      return this.netDeltaPct({ ...d, previous_net_price: prev ? this.getNetPrice(prev) : 0 });
    },
    gridColumns() {
      const soldLbl = this.isMarketStrata ? 'Sold' : 'Leased';
      const editable = (col) => ({
        ...col,
        title: `${col.title} <span class="lsm-edit-icon" title="Double-click to edit">&#9998;</span>`,
        cssClass: 'lsm-editable',
      });
      const num = { hozAlign: 'right', headerHozAlign: 'right' };
      // Extra unit-mix fields (the ones the Unit Mix column chooser offers).
      const fu = this.fromUnitMix;
      const money = (v) => {
        const out = this.lsmMoney(v);
        return out === '—' ? DASH : out;
      };
      const statusValues = { Active: 'Active', Sold: soldLbl, Unreleased: 'Unreleased', None: 'None' };
      const cols = [
        {
          title: 'Listing Type', field: '_type', minWidth: 120,
          formatter: (c) => `<span class="lsm-type-pill"><span class="lsm-type-pill__ring"></span>${esc(this.customListingType(c.getData()))}</span>`,
        },
        editable({
          title: 'Status', field: 'mls_status', minWidth: 100,
          editor: 'list',
          editorParams: { values: statusValues, autocomplete: false, listOnEmpty: true, verticalNavigation: 'editor' },
          formatter: (c) => `<span class="lsm-status ${this.lsmStatusClass(c.getValue())}">${esc(this.lsmStatusLabel(c.getValue()))}</span>`,
        }),
        // Admin-only: the measured size, stored next to Unit Size on each entry.
        this.isAdmin && editable({
          title: 'Actual Size', field: 'mls_actual_size', minWidth: 110, ...num,
          editor: 'input', editorParams: { selectContents: true },
          formatter: (c) => (c.getValue() ? esc(Number(c.getValue()).toLocaleString('en-CA')) : DASH),
        }),
        fu && editable({
          title: 'List Date', field: 'mls_list_date', minWidth: 105,
          editor: dateEditor('-'),
          formatter: (c) => { const v = this.lsmDate(c.getValue()); return v === '—' ? DASH : esc(v); },
        }),
        editable({
          title: 'Price Date', field: 'mls_price_date', minWidth: 110,
          editor: dateEditor(''),
          formatter: (c) => { const v = this.lsmDate(c.getValue()); return v === '—' ? DASH : esc(v); },
        }),
        !this.isRental && {
          title: 'DOM', field: '_dom', ...num,
          formatter: (c) => esc(this.lsmDom(c.getData().mls_price_date)),
        },
        editable({
          title: 'List Price', field: 'mls_list_price', minWidth: 110, ...num,
          editor: 'number', editorParams: { min: 0, step: 1000, selectContents: true },
          formatter: (c) => {
            const style = this.priceBackUpStyle(c.getData().price_back_up);
            const el = c.getElement();
            el.style.color = style.color || '';
            el.style.fontWeight = style.fontWeight || '';
            return money(c.getValue());
          },
        }),
        !this.isRental && {
          title: '$PSF', field: '_psf', ...num,
          formatter: (c) => { const v = this.lsmPsf(c.getData()); return v === '—' ? DASH : v; },
        },
        !this.isRental && editable({
          title: 'Incentives', field: 'buyer_incentives', minWidth: 105, ...num,
          editor: 'number', editorParams: { min: 0, step: 500, selectContents: true },
          formatter: (c) => (Number(c.getValue()) > 0 ? money(c.getValue()) : DASH),
        }),
        fu && editable({
          title: 'Realtor Incentives', field: 'realtor_incentives', minWidth: 145, ...num,
          editor: 'number', editorParams: { min: 0, step: 500, selectContents: true },
          formatter: (c) => (Number(c.getValue()) > 0 ? money(c.getValue()) : DASH),
        }),
        {
          title: 'Net Price', field: '_net', ...num,
          formatter: (c) => `<b>${money(this.getNetPrice(c.getData()))}</b>`,
        },
        {
          title: 'Δ Net', field: '_delta', ...num,
          formatter: (c) => {
            const pct = this.gridNetDelta(c.getData());
            if (pct === null) return DASH;
            const down = pct < 0;
            return `<span class="lsm-delta ${down ? 'lsm-delta--down' : 'lsm-delta--up'}">${down ? SVG_DOWN : SVG_UP}${(pct > 0 ? '+' : '') + pct}%</span>`;
          },
        },
        !this.isRental && {
          title: 'Net $PSF', field: '_netpsf', ...num,
          formatter: (c) => { const v = this.lsmNetPsf(c.getData()); return v === '—' ? DASH : v; },
        },
        editable({
          title: `${soldLbl} Date`, field: 'mls_sold_date', minWidth: 110,
          editor: dateEditor('-'),
          formatter: (c) => { const v = this.lsmDate(c.getValue()); return v === '—' ? DASH : esc(v); },
        }),
        editable({
          title: `${soldLbl} Price`, field: 'mls_sold_price', minWidth: 110, ...num,
          editor: 'number', editorParams: { min: 0, step: 1000, selectContents: true },
          formatter: (c) => money(c.getValue()),
        }),
        fu && !this.isRental && {
          title: `${soldLbl} $PSF`, field: '_soldpsf', ...num,
          formatter: (c) => { const v = this.lsmSoldPsf(c.getData(), false); return v === '—' ? DASH : v; },
        },
        fu && !this.isRental && {
          title: `Net ${soldLbl} $PSF`, field: '_netsoldpsf', minWidth: 120, ...num,
          formatter: (c) => { const v = this.lsmSoldPsf(c.getData(), true); return v === '—' ? DASH : v; },
        },
        fu && {
          title: 'Price Gap', field: '_gap', ...num,
          formatter: (c) => { const g = this.lsmPriceGap(c.getData()); return g == null ? DASH : `${g} d`; },
        },
        fu && {
          title: `${soldLbl} Price Confidence`, field: '_conf', minWidth: 150,
          formatter: (c) => {
            const v = this.lsmPriceConfidence(c.getData());
            if (!v) return DASH;
            const color = { Verified: '#1D5BE0', High: '#15803d', Medium: '#d97706', Low: '#D43847' }[v] || '#9BA8B5';
            return `<span class="lsm-conf-badge" style="color:${color};background:${color}1A">${esc(v)}</span>`;
          },
        },
        fu && editable({
          title: 'Price Status', field: 'price_back_up', minWidth: 135,
          editor: 'list',
          editorParams: { values: PRICE_STATUS_VALUES, autocomplete: false, listOnEmpty: true, verticalNavigation: 'editor' },
          formatter: (c) => {
            const v = c.getValue();
            if (!v) return DASH;
            const st = this.priceBackUpStyle(v);
            return `<span style="color:${st.color || ''};font-weight:${st.fontWeight || ''}">${esc(v)}</span>`;
          },
        }),
        editable({
          title: 'Construction Status', field: 'construction_status', minWidth: 150,
          editor: 'list',
          editorParams: {
            // Read when the editor opens, so options that arrive after the
            // grid was built still show.
            valuesLookup: () => this.constructionStatusOptions.map((o) => o.label),
            autocomplete: false, listOnEmpty: true, verticalNavigation: 'editor',
          },
          formatter: (c) => (c.getValue() ? esc(c.getValue()) : DASH),
        }),
        editable({
          title: 'Completion Date', field: 'completion_date', minWidth: 125,
          editor: dateEditor(''),
          formatter: (c) => { const v = this.lsmDate(c.getValue()); return v === '—' ? DASH : esc(v); },
        }),
        fu && editable({
          title: 'Close Date', field: 'close_date', minWidth: 120,
          editor: dateEditor(''),
          formatter: (c) => { const v = this.lsmDate(c.getValue()); return v === '—' ? DASH : esc(v); },
        }),
        fu && editable({
          title: 'Levels', field: 'levels', minWidth: 105,
          editor: 'list',
          editorParams: { values: Object.keys(LEVEL_TIDS), autocomplete: false, listOnEmpty: true, verticalNavigation: 'editor' },
          formatter: (c) => { const v = c.getValue(); return v && String(v).toLowerCase() !== 'none' ? esc(v) : DASH; },
        }),
        fu && editable({
          title: 'PID', field: 'mls_pid', minWidth: 105,
          editor: 'input', editorParams: { selectContents: true },
          formatter: (c) => (c.getValue() ? esc(c.getValue()) : DASH),
        }),
        // Parking / storage cost are set per project, not per entry, so every
        // row reads them from the unit row the modal was opened with.
        fu && {
          title: 'Parking Cost', field: '_parkcost', minWidth: 110, ...num,
          formatter: () => money(this.row && this.row.custom_parking_stall_cost),
        },
        fu && {
          title: 'Storage Cost', field: '_storecost', minWidth: 110, ...num,
          formatter: () => money(this.row && this.row.custom_storage_locker_cost),
        },
        {
          title: 'Backup File', field: 'backup_file', hozAlign: 'center', headerHozAlign: 'center',
          formatter: (c) => (c.getValue()
            ? `<a href="${esc(c.getValue())}" target="_blank" rel="noopener" class="lsm-backup-link" title="View Backup File">${SVG_FILE}</a>`
            : DASH),
        },
        {
          title: 'Actions', field: '_actions', hozAlign: 'center', headerHozAlign: 'center',
          formatter: () => `<span class="lsm-action lsm-action--edit" title="Edit MLS Entry">${SVG_EDIT}</span><span class="lsm-action-sep">|</span><span class="lsm-action lsm-action--del" title="Delete MLS Entry">${SVG_TRASH}</span>`,
          cellClick: (e, cell) => {
            const d = cell.getData();
            const payload = { projectId: d.cid, projectEntry: d.entry, rowData: d, listingType: this.listingTypeValue };
            if (e.target.closest('.lsm-action--edit')) this.$emit('editMLSEntryNew', payload);
            else if (e.target.closest('.lsm-action--del')) this.$emit('deleteMLSEntry', payload);
          },
        },
        {
          title: 'Last Modified', field: 'last_modified',
          formatter: (c) => (c.getValue() ? esc(c.getValue()) : DASH),
        },
      ];
      return cols.filter(Boolean);
    },
    discardEdits() {
      if (!this._grid) return;
      this._grid.getRows().forEach((row) => {
        const d = row.getData();
        if (this.rowEditedFields(d).length) row.update({ ...this.gridOriginals[d._k] });
      });
      this.gridVersion++;
      this.refreshGrid();
    },
    async fetchConstructionStatuses() {
      try {
        const { data } = await axios.get(`${BACKEND_URL}api/get_construction_status`);
        this.constructionStatusOptions = (Array.isArray(data) ? data : [])
          .filter((d) => d && d.id && d.label)
          .map((d) => ({ id: String(d.id), label: String(d.label) }));
      } catch (e) {
        console.error('ListingSummaryModal: construction statuses', e);
      }
    },
    // presale_mls_entry_edit rewrites the whole entry, so the entry is read
    // fresh first (the same round-trip the MLS Entry Edit page does) and only
    // the fields edited in the grid are replaced.
    async buildEntryEditForm(r, fields) {
      const { data: cur } = await axios.get(`${BACKEND_URL}api/get_mls_by_cid_entry`, {
        params: { cid: r.cid, entry: r.entry },
      });
      if (!cur || typeof cur !== 'object') throw new Error('Could not load the entry');
      const str = (v) => (v == null ? '' : String(v));
      const fd = new FormData();
      const put = (k, v) => fd.append(k, str(v));
      put('cid', r.cid);
      put('entry', r.entry);
      put('fid', (this.$route && this.$route.query.fid) || '');
      put('planName', cur.plan_name);
      put('fp', (Array.isArray(cur.fp) ? cur.fp : []).map((f) => f && f.value).filter(Boolean).join(','));
      put('pr', cur.parking_ref);
      put('fl', cur.fl);
      put('orientation', cur.orientation);
      put('unit', cur.address);
      put('bed', cur.beds);
      put('bath', cur.baths);
      put('unitSize', cur.unit_size);
      put('listPrice', cur.list_price);
      put('parking', cur.parking);
      put('locker', cur.locker);
      put('buyerIncentives', cur.buyer);
      put('realtorIncentives', cur.realtor);
      put('status', cur.status);
      put('soldPrice', cur.sold_price);
      put('soldDate', toIsoDate(cur.sold_date));
      put('listDate', toIsoDate(cur.list_date));
      put('listPriceDate', toIsoDate(cur.list_price_date));
      put('pid', cur.pid);
      put('balcony', cur.balcony);
      put('balconyArea', cur.balconyArea);
      put('priceBackup', cur.priceBackup);
      put('construction_status', cur.construction_status);
      // Unset dates come back as the epoch sentinel; toIsoDate turns that into
      // '' (PHP would read "31-Dec-69" as the year 2069 and store it).
      put('completion_date', toIsoDate(cur.completion_date));
      put('close_date', toIsoDate(cur.close_date));
      put('secret_shopper', cur.secret_shopper);

      const has = (f) => fields.includes(f);
      if (has('mls_status')) {
        const tid = STATUS_TIDS[String(r.mls_status || 'none').trim().toLowerCase()] || STATUS_TIDS.none;
        fd.set('status', String(tid));
      }
      if (has('mls_price_date')) fd.set('listPriceDate', toIsoDate(r.mls_price_date));
      if (has('mls_list_price')) fd.set('listPrice', str(r.mls_list_price));
      if (has('buyer_incentives')) fd.set('buyerIncentives', str(r.buyer_incentives));
      if (has('mls_sold_date')) fd.set('soldDate', toIsoDate(r.mls_sold_date));
      if (has('mls_sold_price')) fd.set('soldPrice', str(r.mls_sold_price));
      if (has('construction_status')) {
        const label = String(r.construction_status || '').trim();
        const opt = this.constructionStatusOptions.find((o) => o.label === label);
        if (!opt) throw new Error(`Unknown construction status "${label}"`);
        fd.set('construction_status', opt.id);
      }
      if (has('completion_date')) fd.set('completion_date', toIsoDate(r.completion_date));
      if (has('mls_list_date')) fd.set('listDate', toIsoDate(r.mls_list_date));
      if (has('realtor_incentives')) fd.set('realtorIncentives', str(r.realtor_incentives));
      if (has('price_back_up')) fd.set('priceBackup', str(r.price_back_up));
      if (has('close_date')) fd.set('close_date', toIsoDate(r.close_date));
      if (has('mls_pid')) fd.set('pid', str(r.mls_pid).trim());
      if (has('mls_actual_size')) fd.set('actualSize', str(r.mls_actual_size).trim());
      if (has('levels')) {
        const tid = LEVEL_TIDS[String(r.levels || '').trim()];
        if (!tid) throw new Error(`Unknown level "${r.levels}"`);
        fd.set('fl', tid);
      }
      return fd;
    },
    async saveEdits() {
      if (this.saving || !this._grid) return;
      const rows = this._grid.getData().filter((r) => this.rowEditedFields(r).length);
      if (!rows.length) return;
      this.saving = true;
      const saved = [];
      let failed = 0;
      try {
        // One entry at a time: each save re-reads its entry, so a later row
        // sees what an earlier one wrote to the same unit.
        for (const r of rows) {
          const fields = this.rowEditedFields(r);
          try {
            const fd = await this.buildEntryEditForm(r, fields);
            const { data } = await axios.post(`${BACKEND_URL}api/presale_mls_entry_edit`, fd);
            if (!data || Number(data.success) !== 1) throw new Error((data && data.message) || 'Save was refused');
            const values = {};
            fields.forEach((f) => { values[f] = r[f]; });
            saved.push({ cid: r.cid, entry: r.entry, values });
            const next = { ...this.gridOriginals, [r._k]: this.pickEditable(r) };
            // Completion / close date are stored once per unit, so every entry
            // of the unit now carries the new value.
            UNIT_WIDE_FIELDS.filter((f) => fields.includes(f)).forEach((f) => {
              this._grid.getRows().forEach((row) => {
                const d = row.getData();
                if (d.cid == r.cid && d._k !== r._k) {
                  row.update({ [f]: r[f] });
                  next[d._k] = { ...next[d._k], [f]: r[f] };
                }
              });
            });
            this.gridOriginals = Object.freeze(next);
          } catch (e) {
            failed++;
            console.error('ListingSummaryModal: save entry', r.cid, r.entry, e);
          }
        }
      } finally {
        this.saving = false;
        this.gridVersion++;
        this.refreshGrid();
      }
      if (saved.length) this.$emit('entries-saved', saved);
      if (this.$bvToast) {
        if (failed) {
          this.$bvToast.toast(`${failed} row${failed !== 1 ? 's' : ''} could not be saved${saved.length ? `; ${saved.length} saved` : ''}`, { variant: 'danger', solid: true });
        } else {
          this.$bvToast.toast(`${saved.length} row${saved.length !== 1 ? 's' : ''} saved`, { variant: 'success', solid: true });
        }
      }
    },

    formatBedsSpec(raw) {
      const v = (raw || '').toString().trim().toLowerCase();
      if (!v) return '—';
      if (v === 's') return 'Studio';
      if (v === 'jr') return 'Jr-Bed';
      return v + '-bed';
    },
    lsmNum(val) {
      const n = toNum(val);
      return n == null ? '—' : Math.round(n).toLocaleString('en-CA');
    },
    lsmMoney(val, isPsf = false) {
      const n = Number(val);
      if (!val || isNaN(n) || n === 0) return '—';
      return isPsf ? '$' + n.toFixed(2) : '$' + Math.round(n).toLocaleString('en-CA');
    },
    // Unset dates come back formatted from the epoch rather than empty, so the
    // sentinels have to read as "not recorded".
    lsmDate(val) {
      const s = (val == null ? '' : String(val)).trim();
      if (!s || s === '-' || s === '1969-12-31' || s === '31-Dec-69') return '—';
      return s;
    },
    lsmDom(priceDate) {
      const listDate = this.row && this.row.mls_list_date;
      if (!listDate || !priceDate) return '—';
      const diff = Math.round((new Date(priceDate) - new Date(listDate)) / 86400000);
      return diff >= 0 ? diff : '—';
    },
    lsmPsf(r) {
      const price = Number(r.mls_list_price);
      const size = Number(r.mls_unit_size);
      if (!price || !size) return '—';
      const psf = price / size;
      return '$' + (this.isMarketStrata ? Math.round(psf).toLocaleString('en-CA') : psf.toFixed(2));
    },
    lsmNetPsf(r) {
      const net = Number(r.mls_list_price) - Number(r.buyer_incentives || 0);
      const size = Number(r.mls_unit_size);
      if (!net || !size) return '—';
      const psf = net / size;
      return '$' + (this.isMarketStrata ? Math.round(psf).toLocaleString('en-CA') : psf.toFixed(2));
    },
    getNetPrice(data) {
      const list = Math.round(Number(data.mls_list_price) || 0);
      const incentive = Math.round(Number(data.buyer_incentives) || 0);
      const result = list - incentive;
      return result > 0 ? result : 0;
    },
    netDeltaPct(r) {
      const prev = r.previous_net_price;
      const curr = this.getNetPrice(r);
      if (!prev || !curr) return null;
      const pct = ((curr - prev) / prev) * 100;
      return isNaN(pct) || pct === 0 ? null : Number(pct.toFixed(2));
    },
    priceBackUpStyle(pb) {
      const map = { 'verified': '#1D5BE0', 'semi-verified': '#E89244', 'educated estimated': '#D43847' };
      const color = map[(pb || '').toLowerCase()];
      return color ? { color, fontWeight: '600' } : {};
    },
    // Same rules as the price gap / confidence columns of the Unit Mix table
    // (ProjectDetail.getPriceGap / getPriceConfidence), applied to one entry.
    isRealVal(v) {
      const s = (v == null ? '' : String(v)).trim();
      return !!s && !['-', '0', 'null', 'undefined', 'NaN', '1969-12-31', '31-Dec-69'].includes(s);
    },
    lsmPriceGap(r) {
      if (!this.isRealVal(r.mls_price_date) || !this.isRealVal(r.mls_sold_date)) return null;
      if ((r.mls_status || '').toLowerCase() !== 'sold') return 0;
      const days = Math.floor(Math.abs(new Date(r.mls_sold_date) - new Date(r.mls_price_date)) / 86400000);
      return isNaN(days) ? null : days;
    },
    lsmPriceConfidence(r) {
      const gap = this.lsmPriceGap(r);
      if (this.isRealVal(r.mls_sold_price) || gap === 0) return 'Verified';
      if (gap == null) return '';
      if (gap <= 91) return 'High';
      if (gap <= 365) return 'Medium';
      return 'Low';
    },
    lsmSoldPsf(r, net) {
      const price = Number(r.mls_sold_price) - (net ? Number(r.buyer_incentives || 0) : 0);
      const size = Number(r.mls_unit_size);
      if (!(Number(r.mls_sold_price) > 0) || !size) return '—';
      const psf = price / size;
      return '$' + (this.isMarketStrata ? Math.round(psf).toLocaleString('en-CA') : psf.toFixed(2));
    },
    lsmStatusClass(status) {
      const s = (status || '').toLowerCase();
      if (s === 'active') return 'lsm-status--active';
      if (s === 'sold') return 'lsm-status--sold';
      return 'lsm-status--other';
    },
    lsmStatusLabel(status) {
      if (!status) return '—';
      if (!this.isMarketStrata && status.toLowerCase() === 'sold') return 'Leased';
      return status;
    },
    customListingType(item) {
      const history = this.gridActive ? this.gridData() : (this.history || []);
      let index = history.findIndex((x) => x.entry == item.entry);
      let res = "";
      const raw = this.rawData || {};
      const row = this.row || {};
      const occupancyDate = raw.occupancy_date ?? row.occupancy_date;
      const soldOutDateRaw = raw.sold_out_date ?? row.sold_out_date;
      let priceDate = new Date(item.mls_price_date);
      let soldDate = new Date(item.mls_sold_date);
      let constructionCompletionDate = new Date(occupancyDate);
      let soldOutDate = new Date(soldOutDateRaw);
      let hasSoldStatusBeforeEntry = false;
      if (index > 0) {
        for (let i = index - 1; i >= 0; i--) {
          if ((history[i].mls_status || "").toLowerCase() == "sold") {
            hasSoldStatusBeforeEntry = true;
            break;
          }
        }
      } else {
        hasSoldStatusBeforeEntry = false;
      }

      res = "Presale";

      if (hasSoldStatusBeforeEntry) {
        if ([priceDate, soldDate].some((date) => date < constructionCompletionDate))
          res = "Assignment";

        if ([priceDate, soldDate].some((date) => date > constructionCompletionDate))
          res = "Resale";
      } else {
        if (
          [priceDate, soldDate].some(
            (date) => date > soldOutDate && date < constructionCompletionDate
          )
        )
          res = "Assignment";

        if (
          [priceDate, soldDate].some(
            (date) => date > soldOutDate && date > constructionCompletionDate
          )
        )
          res = "Resale";
      }

      if (!this.isMarketStrata) {
        switch (res) {
          case "Presale":
            res = "New Lease";
            break;

          case "Resale":
            res = "Re-Lease";
            break;
        }
      }

      return res;
    },
  },
};
</script>

<style>
/* ── Listing Summary Modal ── */
.lsm {
  width: min(1100px, 96vw);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(18, 40, 63, 0.28);
  overflow: hidden;
  border: 1px solid #E9E8E9;
  display: flex;
  flex-direction: column;
  max-height: 92vh;
}

/* Project history carries ~17 columns, so it gets more room before the table
   falls back to horizontal scrolling. */
.lsm--project {
  width: min(1400px, 96vw);
}

/* Header */
.lsm-hdr {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 18px;
  background: #12283F;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.lsm-hdr__rule {
  width: 22px;
  height: 3px;
  background: #FC5868;
  border-radius: 2px;
  flex-shrink: 0;
}

.lsm-hdr__title {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #fff;
  white-space: nowrap;
}

.lsm-hdr__sep {
  width: 1px;
  height: 16px;
  background: rgba(255, 255, 255, 0.18);
}

.lsm-hdr__unit {
  font-family: Poppins, sans-serif;
  color: rgba(255, 255, 255, 0.82);
  font-size: 13px;
  font-weight: 400;
}

.lsm-hdr__unit b {
  color: #fff;
  font-weight: 600;
}

.lsm-unit-link {
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.lsm-unit-link:hover {
  opacity: .8;
}

.lsm-hdr__date {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.lsm-hdr__date-lbl {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 10px;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.55);
}

.lsm-hdr__date-val {
  color: #fff;
  font-weight: 500;
  font-size: 13px;
}

.lsm-hdr__close {
  margin-left: 14px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(255, 255, 255, 0.8);
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.lsm-hdr__close:hover {
  background: rgba(255, 255, 255, 0.10);
  color: #fff;
}

.lsm-hdr__close svg {
  width: 13px;
  height: 13px;
}

/* Specs strip */
.lsm-specs {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  background: #F7F8FB;
  border-bottom: 1px solid #E9E8E9;
  flex-shrink: 0;
  overflow-x: auto;
}

.lsm-spec {
  padding: 10px 12px;
  border-right: 1px solid #E9E8E9;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.lsm-spec:last-child {
  border-right: 0;
}

.lsm-spec--wide {
  grid-column: span 2;
}

.lsm-spec__lbl {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: #343376;
}

.lsm-spec__val {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: #12283F;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Badges wrap instead of being clipped by the ellipsis above */
.lsm-spec__val--badges {
  white-space: normal;
  overflow: visible;
}

.lsm-spec__muted {
  color: #8A8A8A;
  font-weight: 400;
}

/* Toolbar */
.lsm-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-bottom: 1px solid #EEF0F4;
  background: #fff;
  flex-shrink: 0;
}

.lsm-toolbar__lbl {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #545454;
  margin-right: 2px;
}

.lsm-chip {
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid #C9CCD2;
  background: #fff;
  font-family: Poppins, sans-serif;
  font-size: 11px;
  color: #545454;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  transition: all 0.12s;
}

.lsm-chip:hover {
  border-color: #12283F;
  color: #12283F;
}

.lsm-chip--on {
  background: #12283F;
  color: #fff;
  border-color: #12283F;
  font-weight: 500;
}

.lsm-chip__dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #1F9D55;
}

/* Editable grid controls */
.lsm-grid-actions {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.lsm-grid-hint {
  font-family: Poppins, sans-serif;
  font-size: 11px;
  color: #8A8A8A;
  white-space: nowrap;
}

.lsm-grid-hint--dirty {
  color: #B86A1E;
  font-weight: 600;
}

.lsm-grid-btn {
  height: 26px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #1F9D55;
  background: #1F9D55;
  color: #fff;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.04em;
  cursor: pointer;
  white-space: nowrap;
}

.lsm-grid-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.lsm-grid-btn--ghost {
  background: #fff;
  color: #545454;
  border-color: #C9CCD2;
}

.lsm-add-entry--inline {
  margin-left: 0 !important;
}

.lsm-grid.tabulator {
  border: 0;
  margin: 0;
  background: #fff;
  font-family: Poppins, sans-serif;
  font-size: 12px;
  color: #12283F;
}

.lsm-grid .tabulator-header,
.lsm-grid .tabulator-header .tabulator-col {
  background: #fff;
  border-right: 0;
}

.lsm-grid .tabulator-header {
  border-bottom: 1px solid #C9CCD2;
}

.lsm-grid .tabulator-header .tabulator-col .tabulator-col-content {
  padding: 9px 12px;
}

.lsm-grid .tabulator-col-title {
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #545454;
}

.lsm-grid .tabulator-tableholder .tabulator-table {
  background: #fff;
}

.lsm-grid .tabulator-row {
  background: #fff;
  border-bottom: 1px solid #EEF0F4;
}

.lsm-grid .tabulator-row:hover,
.lsm-grid .tabulator-row.tabulator-row-even:hover {
  background: #EDF2FE;
}

.lsm-grid .tabulator-row .tabulator-cell {
  border-right: 0;
  padding: 8px 12px;
  font-variant-numeric: tabular-nums;
}

.lsm-grid .tabulator-cell.lsm-editable {
  cursor: cell;
}

.lsm-grid .tabulator-cell.tabulator-editing {
  border: 1px solid #343376;
  padding: 2px 6px;
  background: #fff;
}

.lsm-grid .tabulator-cell.lsm-cell-edited {
  background: #FFF4DC;
  box-shadow: inset 3px 0 0 #E89244;
}

.lsm-edit-icon {
  opacity: 0.45;
  margin-left: 3px;
  font-size: 10px;
}

.lsm-grid-date {
  width: 100%;
  border: 0;
  outline: none;
  font: inherit;
  background: transparent;
}

.lsm-grid .lsm-action svg,
.lsm-grid .lsm-backup-link svg {
  vertical-align: -2px;
}

html.dark .lsm-grid.tabulator,
html.dark .lsm-grid .tabulator-header,
html.dark .lsm-grid .tabulator-header .tabulator-col,
html.dark .lsm-grid .tabulator-tableholder .tabulator-table,
html.dark .lsm-grid .tabulator-row {
  background: #152030;
  color: #d6e4f0;
}

html.dark .lsm-grid .tabulator-header {
  background: #0E1B2C;
  border-color: rgba(255, 255, 255, 0.1);
}

html.dark .lsm-grid .tabulator-col-title {
  color: rgba(214, 228, 240, 0.55);
}

html.dark .lsm-grid .tabulator-row {
  border-color: rgba(255, 255, 255, 0.05);
}

html.dark .lsm-grid .tabulator-row:hover {
  background: #1d3048;
}

html.dark .lsm-grid .tabulator-cell.lsm-cell-edited {
  background: rgba(232, 146, 68, 0.18);
}

html.dark .lsm-grid-btn--ghost {
  background: transparent;
  color: #d6e4f0;
  border-color: rgba(255, 255, 255, 0.2);
}

.lsm-add-entry {
  margin-left: auto;
  height: 26px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #343376;
  background: #343376;
  color: #fff;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.04em;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  transition: all 0.12s;
  white-space: nowrap;
}

.lsm-add-entry:hover {
  background: #2a2961;
  border-color: #2a2961;
}

.lsm-add-entry svg {
  width: 11px;
  height: 11px;
}

/* Table */
.lsm-table-wrap {
  overflow: auto;
  flex: 1;
  background: #fff;
}

.lsm-table {
  width: 100%;
  border-collapse: collapse;
  font-family: Poppins, sans-serif;
  font-size: 12px;
  color: #12283F;
}

.lsm-table thead th {
  background: #fff;
  text-align: left;
  padding: 9px 12px;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #545454;
  border-bottom: 1px solid #C9CCD2;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}

.lsm-table thead th.num {
  text-align: right;
}

.lsm-table tbody td {
  padding: 0 12px;
  height: 36px;
  border-bottom: 1px solid #EEF0F4;
  white-space: nowrap;
  vertical-align: middle;
}

.lsm-table tbody td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.lsm-table tbody tr:hover td {
  background: #EDF2FE;
}

.lsm-table tbody tr:last-child td {
  border-bottom: 0;
}

.lsm-muted {
  color: #8A8A8A;
}

.lsm-empty-row {
  text-align: center;
  color: #8A8A8A;
  padding: 24px 0 !important;
  height: auto !important;
}

/* Type pill */
.lsm-type-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  border-radius: 999px;
  background: #EDF2FE;
  color: #12283F;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.lsm-type-pill__ring {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #343376;
}

/* Status */
.lsm-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
  font-size: 12px;
}

.lsm-status::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 999px;
}

.lsm-status--active {
  color: #1F9D55;
}

.lsm-status--active::before {
  background: #1F9D55;
}

.lsm-status--sold {
  color: #D43847;
}

.lsm-status--sold::before {
  background: #D43847;
}

.lsm-status--other {
  color: #8A8A8A;
}

.lsm-status--other::before {
  background: #C9CCD2;
}

/* Price confidence badge (unit-mix grid) */
.lsm-conf-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11px;
}

/* Delta */
.lsm-delta {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 999px;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.lsm-delta svg {
  width: 9px;
  height: 9px;
}

.lsm-delta--down {
  background: rgba(212, 56, 71, 0.10);
  color: #D43847;
}

.lsm-delta--up {
  background: rgba(31, 157, 85, 0.10);
  color: #1F9D55;
}

.lsm-delta--inline {
  padding: 1px 6px;
  font-size: 10px;
  vertical-align: middle;
}

/* Backup file column */
.lsm-th-backup {
  text-align: center !important;
  width: 1%;
  white-space: nowrap;
}

.lsm-td-backup {
  text-align: center;
  white-space: nowrap;
}

.lsm-backup-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #343376;
  font-size: 13px;
  padding: 2px 4px;
  transition: opacity 0.12s;
}

.lsm-backup-link+.lsm-backup-link {
  margin-left: 2px;
}

.lsm-backup-link:hover {
  opacity: 0.75;
  color: #343376;
}

html.dark .lsm-backup-link {
  color: #a5a8e8;
}

html.dark .lsm-backup-link:hover {
  color: #a5a8e8;
}

/* Notes column (project mode) — free text, so it truncates instead of forcing
   the table wider; the full value is on the cell's title attribute. */
.lsm-th-notes {
  min-width: 180px;
}

.lsm-td-notes {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Actions column */
.lsm-th-actions {
  text-align: center !important;
  width: 1%;
  white-space: nowrap;
}

.lsm-td-actions {
  text-align: center;
  white-space: nowrap;
}

.lsm-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  padding: 2px 4px;
  transition: opacity 0.12s;
}

.lsm-action:hover {
  opacity: 0.75;
}

.lsm-action--edit {
  color: #343376;
}

.lsm-action--del {
  color: #D43847;
}

.lsm-action-sep {
  color: #C9CCD2;
  margin: 0 4px;
}

html.dark .lsm-action--edit {
  color: #a5a8e8;
}

html.dark .lsm-action--del {
  color: #FC5868;
}

html.dark .lsm-action-sep {
  color: rgba(255, 255, 255, 0.18);
}

/* Incentive pill */
.lsm-incentive {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(252, 88, 104, 0.08);
  color: #FC5868;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

/* Notes column — project history. Long notes truncate; the full text is the
   cell's title. */
.lsm-td-note {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* KPI footer */
/* Features & Costs panel */
.lsm-fc {
  border-top: 1px solid #E9E8E9;
  background: #fff;
  flex-shrink: 0;
}

.lsm-fc-head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 20px;
  border: 0;
  background: #F7F8FB;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #12283F;
  cursor: pointer;
  text-align: left;
}

.lsm-fc-head:hover {
  background: #EDF2FE;
}

.lsm-fc-caret {
  font-size: 10px;
  color: #545454;
}

/* The tab is written as a full-height page pane; here it is a panel inside a
   height-capped modal, so it gives up its own padding and scroller and is held
   to a share of the viewport instead of pushing the KPI footer off-screen. */
.lsm-fc-body {
  max-height: 46vh;
  overflow: auto;
  border-top: 1px solid #E9E8E9;
}

.lsm-fc-body .fc-root {
  padding: 16px 20px 24px;
  min-height: 0;
  overflow-y: visible;
  background: transparent;
}

html.dark .lsm-fc {
  border-color: rgba(255, 255, 255, 0.08);
  background: #152030;
}

html.dark .lsm-fc-head {
  background: #0E1B2C;
  color: #d6e4f0;
}

html.dark .lsm-fc-head:hover {
  background: #1B2A3D;
}

html.dark .lsm-fc-caret {
  color: rgba(214, 228, 240, 0.6);
}

html.dark .lsm-fc-body {
  border-color: rgba(255, 255, 255, 0.08);
}

.lsm-footer {
  display: grid;
  grid-template-columns: repeat(4, 1fr) auto;
  align-items: center;
  gap: 20px;
  padding: 12px 20px;
  background: #F7F8FB;
  border-top: 1px solid #E9E8E9;
  flex-shrink: 0;
}

.lsm-footer--project {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.lsm-kpi__lbl {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #545454;
}

.lsm-kpi__val {
  font-family: Poppins, sans-serif;
  font-weight: 700;
  font-size: 17px;
  color: #12283F;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 4px;
}

.lsm-kpi__unit {
  font-size: 12px;
  color: #8A8A8A;
  font-weight: 400;
}

.lsm-kpi__sub {
  font-family: Poppins, sans-serif;
  font-size: 11px;
  color: #8A8A8A;
  margin-top: 1px;
}

/* ── Overlay & fade transition (shared by all modals) ── */
.sp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(18, 40, 63, 0.55);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;
}

.sp-fade-enter-active,
.sp-fade-leave-active {
  transition: opacity 0.2s;
}

.sp-fade-enter,
.sp-fade-leave-to {
  opacity: 0;
}

/* ── Dark mode ── */
html.dark .lsm {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .lsm-specs {
  background: #0E1B2C;
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .lsm-spec {
  border-color: rgba(255, 255, 255, 0.07);
}

html.dark .lsm-spec__lbl {
  color: #a5a8e8;
}

html.dark .lsm-spec__val {
  color: #d6e4f0;
}

html.dark .lsm-spec__muted {
  color: rgba(214, 228, 240, 0.4);
}

html.dark .lsm-toolbar {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .lsm-toolbar__lbl {
  color: rgba(214, 228, 240, 0.5);
}

html.dark .lsm-chip {
  background: #1d3048;
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(214, 228, 240, 0.7);
}

html.dark .lsm-chip:hover {
  border-color: #d6e4f0;
  color: #d6e4f0;
}

html.dark .lsm-chip--on {
  background: #343376;
  border-color: #343376;
  color: #fff;
}

html.dark .lsm-add-entry {
  background: #FC5868;
  border-color: #FC5868;
  color: #fff;
}

html.dark .lsm-add-entry:hover {
  background: #e54b5b;
  border-color: #e54b5b;
}

html.dark .lsm-table-wrap {
  background: #152030;
}

html.dark .lsm-table {
  color: #d6e4f0;
}

html.dark .lsm-table thead th {
  background: #0E1B2C;
  color: rgba(214, 228, 240, 0.55);
  border-color: rgba(255, 255, 255, 0.1);
}

html.dark .lsm-table tbody td {
  border-color: rgba(255, 255, 255, 0.05);
}

html.dark .lsm-table tbody tr:hover td {
  background: #1d3048;
}

html.dark .lsm-muted {
  color: rgba(214, 228, 240, 0.35);
}

html.dark .lsm-type-pill {
  background: rgba(52, 51, 118, 0.25);
  color: #a5a8e8;
}

html.dark .lsm-footer {
  background: #0E1B2C;
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .lsm-kpi__lbl {
  color: rgba(214, 228, 240, 0.5);
}

html.dark .lsm-kpi__val {
  color: #d6e4f0;
}

html.dark .lsm-kpi__sub {
  color: rgba(214, 228, 240, 0.4);
}

html.dark .lsm-kpi__unit {
  color: rgba(214, 228, 240, 0.4);
}

/* ── Responsive: tablet ── */
@media (max-width: 991.98px) {
  .lsm {
    width: min(1100px, 100vw);
    max-height: 96vh;
  }

  .lsm-specs {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .lsm-spec--wide {
    grid-column: span 2;
  }

  .lsm-footer {
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    padding: 12px 16px;
  }
}

/* ── Responsive: mobile ── */
@media (max-width: 575.98px) {
  .sp-overlay {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }

  .lsm {
    width: 100vw;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
    border: 0;
  }

  /* Header: stack into rows */
  .lsm-hdr {
    flex-wrap: wrap;
    row-gap: 6px;
    column-gap: 10px;
    padding: 10px 12px;
    position: relative;
    padding-right: 44px;
  }

  .lsm-hdr__rule {
    width: 18px;
    height: 3px;
  }

  .lsm-hdr__title {
    font-size: 12px;
    flex: 1 1 auto;
  }

  .lsm-hdr__sep {
    display: none;
  }

  .lsm-hdr__unit {
    flex: 1 1 100%;
    font-size: 12px;
    order: 3;
  }

  .lsm-hdr__date {
    margin-left: 0;
    order: 4;
    flex: 1 1 100%;
    gap: 6px;
  }

  .lsm-hdr__date-lbl {
    font-size: 9px;
  }

  .lsm-hdr__date-val {
    font-size: 12px;
  }

  .lsm-hdr__close {
    position: absolute;
    top: 10px;
    right: 10px;
    margin-left: 0;
  }

  /* Specs strip: wrap into 3-col grid */
  .lsm-specs {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    overflow-x: visible;
  }

  .lsm-spec {
    padding: 8px 10px;
    border-bottom: 1px solid #E9E8E9;
  }

  .lsm-spec:nth-child(3n) {
    border-right: 0;
  }

  .lsm-spec--wide {
    grid-column: span 3;
  }

  .lsm-spec__lbl {
    font-size: 8.5px;
  }

  .lsm-spec__val {
    font-size: 12px;
  }

  /* Toolbar: wrap chips */
  .lsm-toolbar {
    flex-wrap: wrap;
    row-gap: 6px;
    padding: 8px 12px;
  }

  .lsm-add-entry {
    margin-left: 0;
    flex: 1 1 100%;
    justify-content: center;
  }

  /* Table: keep horizontal scroll, tighten padding */
  .lsm-table-wrap {
    -webkit-overflow-scrolling: touch;
  }

  .lsm-table thead th {
    padding: 8px 10px;
    font-size: 9.5px;
  }

  .lsm-table tbody td {
    padding: 0 10px;
    height: 34px;
    font-size: 11.5px;
  }

  /* Footer: stack KPIs */
  .lsm-footer {
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    padding: 12px 14px;
  }

  .lsm-kpi__lbl {
    font-size: 9px;
  }

  .lsm-kpi__val {
    font-size: 14px;
  }

  .lsm-kpi__sub {
    font-size: 10px;
  }
}

html.dark .lsm-spec:nth-child(3n) {
  border-right: 0;
}
</style>
